if (!customElements.get('predictive-search')) {
  customElements.define(
    'predictive-search',
    class PredictiveSearch extends HTMLElement {
      constructor() {
        super();
        this.cachedMap = new Map();
        this.maxCacheEntries = 30; // ODP: bounded LRU cache instead of an ever-growing Map
        this.focusElement = this.input;
        this.searchContent = this.querySelector('.search__content');
        this.searchRecommendationEmpty = this.dataset.searchRecommendationEmpty === 'true';
        this.header = document.querySelector('header');
        this.toggleNavigationButton = document.querySelector('.toggle-navigation-button');

        this.isHeaderSearch = this.closest('header') !== null;

        // ODP: request bookkeeping (stale-response protection) and keyboard state
        this.requestId = 0;
        this.fetchController = null;
        this.activeIndex = -1;
        this.typedValue = '';
        this.renderedQuery = null; // the query the dropdown currently shows results for
        this.effectiveQuery = null; // set when the SKU fallback produced the visible results

        this.recentStorageKey = 'odp:recent-searches';
        this.maxRecentSearches = 5;

        this.states = {
          OPEN: 'predictive-search-open',
          LOADING: 'btn--loading',
          SEARCH_OPEN: 'search-open',
          ACTIVE: 'is-active'
        };
      }

      connectedCallback() {
        // The header and the search page re-render in the theme editor: the document listeners must go with them.
        this.abortController = new AbortController();
        const { signal } = this.abortController;

        this.resetButton?.addEventListener('click', this.clear.bind(this), { signal });
        this.input.addEventListener('input', FoxTheme.utils.debounce(this.onChange.bind(this), 200), { signal });
        this.input.addEventListener('focus', this.onFocus.bind(this), { signal });
        this.input.addEventListener('keydown', this.onKeydown.bind(this), { signal });
        document.addEventListener('click', this.handleClickOutside.bind(this), { signal });
        this.searchProductTypes?.addEventListener('change', this.handleProductTypeChange.bind(this), { signal });
        this.form?.addEventListener('submit', this.onSubmit.bind(this), { signal });

        // Save the query as a recent search whenever a result link is followed.
        this.addEventListener('click', this.onResultClick.bind(this), { signal });
        this.recentClearButton?.addEventListener('click', this.clearRecentSearches.bind(this), { signal });

        document.addEventListener(
          'menu-drawer:open',
          () => {
            this.classList.remove('predictive-search-open');
            document.body.classList.remove('search-open');
            this.setExpanded(false);
          },
          { signal }
        );

        this.renderRecentSearches();
      }

      disconnectedCallback() {
        this.abortController?.abort();
        this.fetchController?.abort();
        this.cachedMap.clear();
      }

      get input() {
        return this.querySelector('input[type="search"]');
      }
      get resetButton() {
        return this.querySelector('button[type="reset"]');
      }

      get searchProductTypes() {
        return this.querySelector('.search__types select');
      }

      get form() {
        return this.querySelector('form');
      }

      get resultsElement() {
        return document.getElementById(`PredictiveSearchResults-${FoxTheme.utils.getSectionId(this)}`);
      }

      get recommendationsElement() {
        return this.querySelector('.search__recommendations');
      }

      get statusElement() {
        return this.querySelector('[data-ps-status]');
      }

      get recentWrapper() {
        return this.querySelector('[data-ps-recent]');
      }

      get recentList() {
        return this.querySelector('[data-ps-recent-list]');
      }

      get recentClearButton() {
        return this.querySelector('[data-ps-recent-clear]');
      }

      get headerCollapseOnScroll() {
        return this.header.dataset.collapseOnScroll === 'true';
      }

      hasRecommendations() {
        return !this.searchRecommendationEmpty || this.getRecentSearches().length > 0;
      }

      onFocus(event) {
        document.documentElement.style.setProperty('--viewport-height', `${window.innerHeight}px`);
        document.documentElement.style.setProperty(
          '--header-bottom-position',
          `${parseInt(this.header.getBoundingClientRect().bottom)}px`
        );
        const hasRecommendations = this.hasRecommendations();
        if (this.activeIndex === -1) this.typedValue = this.input.value;

        if (this.isHeaderSearch && hasRecommendations) {
          document.body.classList.add('search-open');
          this.toggleNavButtonVisibility(false);
        }

        if (hasRecommendations) {
          this.searchContent.classList.remove('hidden');
        }
        this.classList.add('predictive-search-open');
        if (this.getQuery().length === 0) {
          if (!hasRecommendations) {
            this.searchContent.classList.add('hidden');
            this.setExpanded(false);
          } else {
            this.setExpanded(true);
          }
          return;
        }
        // Reopening shows the results of a query the buyer already searched: not a search update.
        this.renderSection(this.getQuery(), { isReopen: true });
      }

      getQuery() {
        return this.input.value.trim();
      }

      clear(event = null) {
        event?.preventDefault();
        // Cancel any in-flight request so a late response can't reopen the results.
        this.requestId++;
        this.fetchController?.abort();
        this.setLoadingState(false);

        this.input.value = '';
        this.typedValue = '';
        this.renderedQuery = null;
        this.effectiveQuery = null;
        this.resetActive();
        this.input.focus();
        this.removeAttribute('results');
        this.toggleSearchState(false);
      }

      handleProductTypeChange(evt) {
        const query = this.getQuery();
        if (query.length > 0) {
          this.renderSection(query);
        }
      }

      onSubmit(event) {
        const query = this.getQuery();
        if (query.length === 0) return;

        const resultsAreCurrent = this.hasAttribute('results') && this.renderedQuery === query;

        // SKU direct hit: one exact product-code match → go straight to that product.
        if (resultsAreCurrent && this.activeIndex === -1) {
          const exactMatches = this.resultsElement?.querySelectorAll('[data-sku-exact="true"]') || [];
          if (exactMatches.length === 1) {
            event.preventDefault();
            this.saveRecentSearch(query);
            window.location.href = exactMatches[0].href;
            return;
          }
        }

        this.saveRecentSearch(query);

        // If the dropdown only found results via the SKU fallback, submit that form of the query.
        const submitQuery = resultsAreCurrent && this.effectiveQuery ? this.effectiveQuery : query;
        const productType = this.searchProductTypes?.value;

        if (!productType && submitQuery === query) return; // native submit is fine
        if (query.startsWith('product_type:')) return;

        event.preventDefault();
        const params = new URLSearchParams();
        params.set('q', productType ? `product_type:${productType} AND ${submitQuery}` : submitQuery);
        params.set('options[prefix]', 'last');
        window.location.href = `${this.form.getAttribute('action')}?${params.toString()}`;
      }

      setupURL(query) {
        const url = new URL(`${window.shopUrl}${FoxTheme.routes.predictive_search_url}`);
        let searchTerm = query;
        if (this.searchProductTypes && this.searchProductTypes.value != '') {
          // URLSearchParams encodes once; the previous encodeURIComponent here double-encoded
          // the term, so "canon r5" was actually searched as "canon%20r5".
          searchTerm = `product_type:${this.searchProductTypes.value} AND ${query}`;
        }
        url.searchParams.set('q', searchTerm);
        url.searchParams.set('resources[limit]', this.dataset.resultsLimit || 3);
        url.searchParams.set('resources[limit_scope]', 'each');
        url.searchParams.set('section_id', FoxTheme.utils.getSectionId(this));
        return url.toString();
      }

      onChange() {
        const query = this.getQuery();
        this.typedValue = this.input.value;
        this.resetActive();
        if (query.length === 0) {
          this.clear();
          return;
        }
        this.renderSection(query);
      }

      async renderSection(query, { isReopen = false } = {}) {
        const searchUpdate = isReopen
          ? null
          : FoxTheme.storefrontEvents?.start(this, 'SearchUpdateEvent', {
              search: { query }
            });

        const requestId = ++this.requestId;
        this.fetchController?.abort();
        const controller = new AbortController();
        this.fetchController = controller;
        this.setLoadingState(true);

        try {
          let usedQuery = query;
          let responseText = await this.fetchSection(this.setupURL(query), controller.signal);

          // SKU fallback: retry product-code-like queries with separators normalised.
          if (this.countResults(responseText) === 0) {
            const fallbackQuery = this.getSkuFallbackQuery(query);
            if (fallbackQuery) {
              const fallbackText = await this.fetchSection(this.setupURL(fallbackQuery), controller.signal);
              if (this.countResults(fallbackText) > 0) {
                responseText = fallbackText;
                usedQuery = fallbackQuery;
              }
            }
          }

          if (requestId !== this.requestId) return; // a newer search has started

          this.renderedQuery = query;
          this.effectiveQuery = usedQuery !== query ? usedQuery : null;
          this.renderSearchResults(responseText);
          this.renderFallbackNotice();
          this.announceResults();
          searchUpdate?.resolve({ totalCount: this.getTotalResults() });
        } catch (error) {
          searchUpdate?.reject(error);
          if (error.name === 'AbortError') return;
          console.error('Error fetching data: ', error);
          this.setAttribute('error', 'Failed to load data');
        } finally {
          if (requestId === this.requestId) this.setLoadingState(false);
        }
      }

      async fetchSection(url, signal) {
        if (this.cachedMap.has(url)) {
          // Refresh recency so frequently used entries survive eviction.
          const cached = this.cachedMap.get(url);
          this.cachedMap.delete(url);
          this.cachedMap.set(url, cached);
          return cached;
        }

        const response = await fetch(url, { signal });
        if (!response.ok) throw new Error('Network response was not ok');
        const responseText = await response.text();

        this.cachedMap.set(url, responseText);
        if (this.cachedMap.size > this.maxCacheEntries) {
          this.cachedMap.delete(this.cachedMap.keys().next().value);
        }
        return responseText;
      }

      countResults(responseText) {
        const match = responseText.match(/data-total-results="(\d+)"/);
        return match ? Number(match[1]) : 0;
      }

      /**
       * Shopify tokenises SKUs on separators, so "AENDF-001" is indexed as "aendf" + "001".
       * - Query with separators/spaces ("89E-965") → collapse them ("89E965").
       * - Run-together code ("aendf001") → split at letter/digit boundaries ("aendf 001").
       * Only used when the original query returned nothing, and only for codes containing a digit.
       */
      getSkuFallbackQuery(query) {
        const q = query.trim();
        if (q.length < 4 || !/\d/.test(q)) return null;

        if (/[\s\-\/._]/.test(q)) {
          const collapsed = q.replace(/[\s\-\/._]+/g, '');
          return collapsed.length >= 4 && collapsed !== q ? collapsed : null;
        }

        if (/^[a-z0-9]+$/i.test(q) && /[a-z]/i.test(q)) {
          const split = q.replace(/([a-z])(?=\d)|(\d)(?=[a-z])/gi, '$1$2 ').trim();
          return split !== q ? split : null;
        }

        return null;
      }

      getTotalResults() {
        const results = this.resultsElement;
        return Number(results?.querySelector('[data-total-results]')?.dataset.totalResults) || 0;
      }

      renderSearchResults(responseText) {
        const id = 'PredictiveSearchResults-' + FoxTheme.utils.getSectionId(this);
        const targetElement = document.getElementById(id);

        if (targetElement) {
          const parser = new DOMParser();
          const parsedDoc = parser.parseFromString(responseText, 'text/html');
          const contentElement = parsedDoc.getElementById(id);

          if (contentElement) {
            this.searchContent?.classList.remove('hidden');
            targetElement.innerHTML = contentElement.innerHTML;
            this.resetActive();

            if (this.isHeaderSearch && !document.body.classList.contains('search-open')) {
              document.body.classList.add('search-open');
              this.toggleNavButtonVisibility(true);
              this.classList.add('predictive-search-open');
            } else {
              this.classList.add('predictive-search-open');
            }
            this.setAttribute('results', 'true');
            this.setExpanded(true);
          } else {
            console.error(`Element with id '${id}' not found in the parsed response.`);
          }
        } else {
          console.error(`Element with id '${id}' not found in the document.`);
        }
      }

      renderFallbackNotice() {
        if (!this.effectiveQuery) return;
        const target = this.resultsElement;
        if (!target) return;
        const notice = document.createElement('p');
        notice.className = 'ps-fallback-notice';
        notice.append(`${this.dataset.textShowingFor || 'Showing results for'} `);
        const strong = document.createElement('strong');
        strong.textContent = this.effectiveQuery;
        notice.append(strong);
        target.prepend(notice);
      }

      announceResults() {
        const status = this.statusElement;
        if (!status) return;
        const total = this.getTotalResults();
        status.textContent =
          total > 0
            ? `${total} ${this.dataset.textResultsAvailable || 'results available.'}`
            : this.dataset.textNoResults || 'No results found.';
      }

      /* ---------- Keyboard navigation (WAI-ARIA combobox pattern) ---------- */

      onKeydown(event) {
        // Remember what the buyer actually typed before arrowing through suggestions.
        if (this.activeIndex === -1 && (event.key === 'ArrowDown' || event.key === 'ArrowUp')) {
          this.typedValue = this.input.value;
        }
        switch (event.key) {
          case 'ArrowDown':
            event.preventDefault();
            if (!this.classList.contains(this.states.OPEN)) {
              this.onFocus();
              return;
            }
            this.moveActive(1);
            break;
          case 'ArrowUp':
            event.preventDefault();
            this.moveActive(-1);
            break;
          case 'Enter': {
            const active = this.getNavigableItems()[this.activeIndex];
            if (active) {
              event.preventDefault();
              this.saveRecentSearch(active.dataset.psQuery || this.typedValue || this.getQuery());
              active.click();
            }
            break;
          }
          case 'Escape':
            if (this.classList.contains(this.states.OPEN)) {
              event.preventDefault();
              this.restoreTypedValue();
              this.resetActive();
              this.toggleSearchState(false);
            }
            break;
        }
      }

      getNavigableItems() {
        const scope = this.hasAttribute('results') ? this.resultsElement : this.recommendationsElement;
        if (!scope) return [];
        return Array.from(scope.querySelectorAll('[data-ps-item], .product-card__title a')).filter(
          (el) => el.offsetParent !== null
        );
      }

      moveActive(direction) {
        const items = this.getNavigableItems();
        if (items.length === 0) return;

        let next = this.activeIndex + direction;
        if (next >= items.length) next = -1; // wrap back to the input
        if (next < -1) next = items.length - 1;
        this.setActive(next, items);
      }

      setActive(index, items = this.getNavigableItems()) {
        const previous = items[this.activeIndex];
        if (previous) {
          previous.classList.remove(this.states.ACTIVE);
          if (previous.hasAttribute('aria-selected')) previous.setAttribute('aria-selected', 'false');
        }

        this.activeIndex = index;
        const current = items[index];

        if (!current) {
          this.input.removeAttribute('aria-activedescendant');
          this.restoreTypedValue();
          return;
        }

        if (!current.id) current.id = `PredictiveSearchItem-${FoxTheme.utils.getSectionId(this)}-${index}`;
        current.classList.add(this.states.ACTIVE);
        if (current.hasAttribute('aria-selected')) current.setAttribute('aria-selected', 'true');
        this.input.setAttribute('aria-activedescendant', current.id);
        current.scrollIntoView({ block: 'nearest' });

        // Baymard: copy the highlighted query suggestion into the field so it can be refined.
        if (current.dataset.psQuery) {
          this.input.value = current.dataset.psQuery;
        } else {
          this.restoreTypedValue();
        }
      }

      resetActive() {
        const items = this.getNavigableItems();
        items.forEach((el) => {
          el.classList.remove(this.states.ACTIVE);
          if (el.hasAttribute('aria-selected')) el.setAttribute('aria-selected', 'false');
        });
        this.activeIndex = -1;
        this.input.removeAttribute('aria-activedescendant');
      }

      restoreTypedValue() {
        if (this.input.value !== this.typedValue) this.input.value = this.typedValue;
      }

      setExpanded(isExpanded) {
        if (!this.input.hasAttribute('role')) return;
        this.input.setAttribute('aria-expanded', isExpanded ? 'true' : 'false');
        const controls = this.hasAttribute('results') ? this.resultsElement : this.recommendationsElement;
        if (controls?.id) this.input.setAttribute('aria-controls', controls.id);
      }

      /* ---------- Recent searches (localStorage, per browser) ---------- */

      getRecentSearches() {
        try {
          const stored = JSON.parse(window.localStorage.getItem(this.recentStorageKey) || '[]');
          return Array.isArray(stored) ? stored.filter((item) => typeof item === 'string') : [];
        } catch (error) {
          return [];
        }
      }

      saveRecentSearch(query) {
        const term = (query || '').trim();
        if (term.length < 2 || term.startsWith('product_type:')) return;
        const recent = this.getRecentSearches().filter((item) => item.toLowerCase() !== term.toLowerCase());
        recent.unshift(term);
        try {
          window.localStorage.setItem(this.recentStorageKey, JSON.stringify(recent.slice(0, this.maxRecentSearches)));
        } catch (error) {
          // Storage unavailable (private mode, blocked): recent searches are a convenience only.
        }
        this.renderRecentSearches();
      }

      clearRecentSearches(event) {
        event?.preventDefault();
        try {
          window.localStorage.removeItem(this.recentStorageKey);
        } catch (error) {
          // ignore
        }
        this.renderRecentSearches();
        this.input.focus();
        if (!this.hasRecommendations()) this.toggleSearchState(false);
      }

      renderRecentSearches() {
        const wrapper = this.recentWrapper;
        const list = this.recentList;
        if (!wrapper || !list) return;

        const recent = this.getRecentSearches();
        list.replaceChildren();
        wrapper.classList.toggle('hidden', recent.length === 0);

        const action = this.form?.getAttribute('action') || '/search';
        const sectionId = FoxTheme.utils.getSectionId(this);

        recent.forEach((term, index) => {
          const params = new URLSearchParams({ q: term, 'options[prefix]': 'last' });
          const item = document.createElement('li');
          const link = document.createElement('a');
          link.href = `${action}?${params.toString()}`;
          link.id = `PredictiveSearchRecent-${sectionId}-${index}`;
          link.className = 'reversed-link';
          link.setAttribute('role', 'option');
          link.setAttribute('aria-selected', 'false');
          link.dataset.psItem = '';
          link.dataset.psQuery = term;
          link.textContent = term;
          item.append(link);
          list.append(item);
        });
      }

      onResultClick(event) {
        const link = event.target.closest('a[href]');
        if (!link || !this.searchContent?.contains(link)) return;
        this.saveRecentSearch(link.dataset.psQuery || this.typedValue || this.getQuery());
      }

      /* ---------- Open / close ---------- */

      handleClickOutside(event) {
        const target = event.target;
        const shouldClose = this.isHeaderSearch
          ? !this.contains(target) &&
            ((target.classList.contains('fixed-overlay') && target.closest('.header-section')) ||
              target.classList.contains('header__search-close'))
          : !this.contains(target);

        if (shouldClose) {
          setTimeout(() => this.toggleSearchState(false));
        }
      }

      toggleSearchState(isOpen) {
        this.classList.toggle(this.states.OPEN, isOpen);
        if (this.isHeaderSearch) {
          document.body.classList.toggle(this.states.SEARCH_OPEN, isOpen);
          this.toggleNavButtonVisibility(!isOpen);
        }
        if (!isOpen) {
          this.resetActive();
          if (!this.hasRecommendations()) this.searchContent?.classList.add('hidden');
        }
        this.setExpanded(isOpen);
      }

      toggleNavButtonVisibility(isOpen) {
        this.toggleNavigationButton &&
          this.headerCollapseOnScroll &&
          this.toggleNavigationButton.classList.toggle('is-show', isOpen);
      }

      setLoadingState(isLoading) {
        if (isLoading) {
          this.setAttribute('loading', 'true');
          this.resetButton?.classList.add(this.states.LOADING);
        } else {
          this.removeAttribute('loading');
          this.resetButton?.classList.remove(this.states.LOADING);
        }
      }
    }
  );
}